//
//  Shell.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 21, 2026
//  Author: Bob Keathley
//
//  Shared chrome for both consoles: brand, nav, who-am-i, change password and
//  sign out. Each app supplies its own nav per plane and an optional banner.
//
import { useState, type ReactNode } from 'react'
import { auth, type Session } from '../auth'
import { href } from '../router'
import BrandMark from './BrandMark'
import ChangePassword from './ChangePassword'

export type NavItem = { path: string; label: string; show?: boolean }

/**
 * The chrome common to both consoles: brand, nav, who-am-i, sign out. Each app supplies its own
 * nav items (they differ per plane -- 27 §8) and may render a banner (e.g. the tenant app's
 * assumed-session banner) above the page body.
 */
export default function Shell({
  session,
  path,
  nav,
  banner,
  actions,
  children,
}: {
  session: Session
  path: string
  nav: NavItem[]
  banner?: ReactNode
  /** Extra controls rendered first inside the who-am-i cluster (the tenant app's notification bell). */
  actions?: ReactNode
  children: ReactNode
}) {
  const u = session.user
  const [changing, setChanging] = useState(false)
  const matches = (p: string) => (p === '/' ? path === '/' : path.startsWith(p))
  // The longest matching nav path wins, so a nested item (My Notebook) does not also light its parent (Hierarchy).
  const best = nav.filter((n) => n.show !== false && matches(n.path)).reduce((a, n) => (n.path.length > a.length ? n.path : a), '')
  const active = (p: string) => p === best
  return (
    <div className="shell">
      <header className="top">
        <a className="brand" href={href('/')} style={{ fontSize: 18, textDecoration: 'none', color: 'inherit' }}>
          <BrandMark variant="shield" size={28} />
          <span><b>Signet</b> Console</span>
        </a>
        <nav aria-label="Sections">
          {nav.filter((n) => n.show !== false).map((n) => (
            <a key={n.path} className={active(n.path) ? 'on' : ''} href={href(n.path)}>{n.label}</a>
          ))}
        </nav>
        <div className="who">
          {actions}
          <span className="mono">{u.email}</span>
          <span className={`pill ${u.role}`}>{u.role}</span>
          <button className="btn ghost sm" onClick={() => setChanging((c) => !c)}>Change password</button>
          <button className="btn ghost sm" onClick={() => auth.logout()}>Sign out</button>
        </div>
      </header>
      {banner}
      <main className="page">{changing ? <ChangePassword session={session} onDone={() => setChanging(false)} onCancel={() => setChanging(false)} /> : children}</main>
    </div>
  )
}
